import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Brain, Home, FileText, Download, Trophy, Calendar, Loader2, AlertCircle, ArrowLeft, Eye, X, ChevronDown, ChevronUp, Lightbulb, AlertTriangle, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MyEvaluations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEvaluation, setExpandedEvaluation] = useState(null); // Track which evaluation is expanded
  const [showFullReportModal, setShowFullReportModal] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    fetchEvaluations();
  }, [user, navigate]);

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/evaluations/user`, {
        credentials: 'include'
      });

      if (response.status === 401) {
        navigate('/auth');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch evaluations');
      }

      const data = await response.json();
      setEvaluations(data.evaluations || []);
    } catch (err) {
      console.error('Error fetching evaluations:', err);
      setError('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (evaluationId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/evaluations/${evaluationId}/pdf`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IF_Evaluation_${evaluationId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "PDF Downloaded", description: "Report also sent to your email" });
    } catch (error) {
      console.error('PDF download error:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 8.5) return 'bg-purple-600';
    if (score >= 7) return 'bg-green-600';
    if (score >= 5) return 'bg-blue-600';
    return 'bg-orange-600';
  };

  const handleViewFullReport = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setShowFullReportModal(true);
  };

  const toggleExpanded = (evaluationId) => {
    setExpandedEvaluation(expandedEvaluation === evaluationId ? null : evaluationId);
  };

  // Full Report Modal Component
  const FullReportModal = ({ evaluation, isOpen, onClose }) => {
    if (!isOpen || !evaluation) return null;
    
    const fullEval = evaluation.full_evaluation || {};
    const oi = fullEval.oi || {};
    const msa = fullEval.msa || {};
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-2">{evaluation.paper_title}</h2>
                <div className="flex items-center gap-3 text-sm opacity-90">
                  <span>ID: {evaluation.evaluation_id}</span>
                  <span>•</span>
                  <span>{formatDate(evaluation.created_at)}</span>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* OI Score Display */}
            <div className="mt-4 flex items-center gap-6">
              <div className="bg-white/20 rounded-lg px-6 py-3 text-center">
                <div className="text-4xl font-bold">{evaluation.oi_total.toFixed(1)}</div>
                <div className="text-sm opacity-90">OI Score</div>
              </div>
              <div className="text-sm">
                <div className="font-semibold text-lg">{evaluation.oi_interpretation}</div>
                <div className="opacity-90">Originality Index Assessment</div>
              </div>
            </div>
          </div>
          
          {/* Content - Scrollable */}
          <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
            {/* Paper Summary */}
            {(fullEval.paper_summary || evaluation.paper_abstract) && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Paper Summary
                </h3>
                <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">
                  {fullEval.paper_summary || evaluation.paper_abstract}
                </p>
              </div>
            )}
            
            {/* Dimension Scores Grid */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {/* Novelty */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-purple-900">Novelty</h4>
                  <span className="text-2xl font-bold text-purple-600">{evaluation.oi_novelty.toFixed(1)}/10</span>
                </div>
                {oi.novelty_reasoning && (
                  <p className="text-sm text-slate-600 mb-3">{oi.novelty_reasoning}</p>
                )}
                {(oi.novelty_strengths?.length > 0 || oi.key_strengths?.length > 0) && (
                  <div className="mb-2">
                    <div className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" /> Strengths
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {(oi.novelty_strengths || oi.key_strengths || []).slice(0, 3).map((s, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-green-500 mt-0.5">•</span> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(oi.novelty_weaknesses?.length > 0 || oi.key_weaknesses?.length > 0) && (
                  <div>
                    <div className="text-xs font-semibold text-orange-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Areas for Improvement
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {(oi.novelty_weaknesses || oi.key_weaknesses || []).slice(0, 2).map((w, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-orange-500 mt-0.5">•</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Resonance */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-blue-900">Resonance</h4>
                  <span className="text-2xl font-bold text-blue-600">{evaluation.oi_resonance.toFixed(1)}/10</span>
                </div>
                {oi.resonance_reasoning && (
                  <p className="text-sm text-slate-600 mb-3">{oi.resonance_reasoning}</p>
                )}
                {oi.resonance_strengths?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" /> Strengths
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {oi.resonance_strengths.slice(0, 3).map((s, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-green-500 mt-0.5">•</span> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {oi.resonance_weaknesses?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-orange-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Areas for Improvement
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {oi.resonance_weaknesses.slice(0, 2).map((w, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-orange-500 mt-0.5">•</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Synthesis */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-green-900">Synthesis</h4>
                  <span className="text-2xl font-bold text-green-600">{evaluation.oi_synthesis.toFixed(1)}/10</span>
                </div>
                {oi.synthesis_reasoning && (
                  <p className="text-sm text-slate-600 mb-3">{oi.synthesis_reasoning}</p>
                )}
                {oi.synthesis_strengths?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" /> Strengths
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {oi.synthesis_strengths.slice(0, 3).map((s, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-green-500 mt-0.5">•</span> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {oi.synthesis_weaknesses?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-orange-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Areas for Improvement
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {oi.synthesis_weaknesses.slice(0, 2).map((w, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-orange-500 mt-0.5">•</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Ethical */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-orange-900">Ethical Anchoring</h4>
                  <span className="text-2xl font-bold text-orange-600">{evaluation.oi_ethical.toFixed(1)}/10</span>
                </div>
                {oi.ethical_reasoning && (
                  <p className="text-sm text-slate-600 mb-3">{oi.ethical_reasoning}</p>
                )}
                {oi.ethical_strengths?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" /> Strengths
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {oi.ethical_strengths.slice(0, 3).map((s, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-green-500 mt-0.5">•</span> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {oi.ethical_weaknesses?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-orange-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Areas for Improvement
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {oi.ethical_weaknesses.slice(0, 2).map((w, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-orange-500 mt-0.5">•</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            
            {/* MSA Section */}
            <div className="border rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-600" />
                Mirror-Spark Assessment (MSA)
              </h3>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1 bg-gradient-to-r from-blue-100 to-orange-100 rounded-full h-4 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                    style={{ width: `${evaluation.msa_mirror_pct}%` }}
                  />
                </div>
                <div className="text-sm font-medium min-w-[120px]">
                  <span className="text-blue-600">{evaluation.msa_mirror_pct.toFixed(0)}% Mirror</span>
                  <span className="text-slate-400 mx-1">|</span>
                  <span className="text-orange-600">{evaluation.msa_spark_pct.toFixed(0)}% Spark</span>
                </div>
              </div>
              <div className="text-sm text-slate-600">
                <span className="font-semibold">Assessment: </span>
                {evaluation.msa_assessment}
              </div>
              {msa.interpretation && (
                <p className="text-sm text-slate-600 mt-2">{msa.interpretation}</p>
              )}
            </div>
            
            {/* Key Innovations / Recommendations if available */}
            {(oi.key_innovations?.length > 0 || fullEval.improvement_recommendations?.length > 0) && (
              <div className="grid md:grid-cols-2 gap-4">
                {oi.key_innovations?.length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-2">Key Innovations</h4>
                    <ul className="text-sm text-slate-700 space-y-1">
                      {oi.key_innovations.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-purple-500">✦</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {fullEval.improvement_recommendations?.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Improvement Recommendations</h4>
                    <ul className="text-sm text-slate-700 space-y-1">
                      {fullEval.improvement_recommendations.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-blue-500">→</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="border-t p-4 bg-slate-50 flex justify-between items-center">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={() => handleDownloadPDF(evaluation.evaluation_id)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF Certificate
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading your evaluations...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-xl font-bold">My Evaluations</h1>
              <p className="text-sm text-slate-600">Your paper evaluation history</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/quick-evaluate')}>
              <Trophy className="w-4 h-4 mr-2" />
              New Evaluation
            </Button>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Stats Summary */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold mb-1">{evaluations.length}</div>
              <div className="text-sm opacity-90">Total Evaluations</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-cyan-500 text-white">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold mb-1">
                {evaluations.length > 0 
                  ? (evaluations.reduce((sum, e) => sum + e.oi_total, 0) / evaluations.length).toFixed(1)
                  : '0.0'
                }
              </div>
              <div className="text-sm opacity-90">Average OI Score</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold mb-1">
                {evaluations.filter(e => e.evaluation_type === 'initial').length}
              </div>
              <div className="text-sm opacity-90">Initial Evaluations</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold mb-1">
                {evaluations.filter(e => e.evaluation_type === 'transformed').length}
              </div>
              <div className="text-sm opacity-90">Transformed Papers</div>
            </CardContent>
          </Card>
        </div>

        {/* Error State */}
        {error && (
          <Card className="mb-6 border-2 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <p className="text-red-800">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Evaluations List */}
        {evaluations.length === 0 ? (
          <Card className="text-center">
            <CardContent className="pt-12 pb-12">
              <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Evaluations Yet</h3>
              <p className="text-slate-600 mb-6">
                Start by evaluating your first paper to see it appear here
              </p>
              <Button
                onClick={() => navigate('/quick-evaluate')}
                className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Evaluate Your First Paper
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              All Evaluations ({evaluations.length})
            </h2>
            
            {evaluations.map((evaluation) => (
              <Card key={evaluation.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    {/* Left side - Paper info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-3">
                        <FileText className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-slate-900 mb-1">
                            {evaluation.paper_title}
                          </h3>
                          <p className="text-sm text-slate-600 line-clamp-2">
                            {evaluation.paper_abstract}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {formatDate(evaluation.created_at)}
                        </div>
                        
                        <Badge variant="outline" className="text-xs">
                          {evaluation.paper_type}
                        </Badge>
                        
                        {evaluation.evaluation_type === 'transformed' && (
                          <Badge className="bg-emerald-600 text-white text-xs">
                            ✨ Transformed
                          </Badge>
                        )}
                        
                        <span className="text-xs text-slate-500">
                          ID: {evaluation.evaluation_id}
                        </span>
                      </div>
                    </div>

                    {/* Right side - Score and actions */}
                    <div className="flex md:flex-col items-center gap-4">
                      {/* OI Score Badge */}
                      <div className="text-center">
                        <div className={`${getScoreBadgeColor(evaluation.oi_total)} text-white rounded-lg px-6 py-3 mb-2`}>
                          <div className="text-sm opacity-90">OI Score</div>
                          <div className="text-3xl font-bold">{evaluation.oi_total.toFixed(1)}</div>
                          <div className="text-xs opacity-90">/10</div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {evaluation.oi_interpretation}
                        </Badge>
                      </div>

                      {/* Download button */}
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => handleViewFullReport(evaluation)}
                          variant="outline"
                          className="border-purple-300 text-purple-700 hover:bg-purple-50 whitespace-nowrap"
                          size="sm"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Full Report
                        </Button>
                        <Button
                          onClick={() => handleDownloadPDF(evaluation.evaluation_id)}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white whitespace-nowrap"
                          size="sm"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Dimension scores */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center p-2 bg-purple-50 rounded">
                        <div className="text-xs text-purple-700 mb-1">Novelty</div>
                        <div className="text-lg font-bold text-purple-900">{evaluation.oi_novelty.toFixed(1)}</div>
                      </div>
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <div className="text-xs text-blue-700 mb-1">Resonance</div>
                        <div className="text-lg font-bold text-blue-900">{evaluation.oi_resonance.toFixed(1)}</div>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="text-xs text-green-700 mb-1">Synthesis</div>
                        <div className="text-lg font-bold text-green-900">{evaluation.oi_synthesis.toFixed(1)}</div>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded">
                        <div className="text-xs text-orange-700 mb-1">Ethical</div>
                        <div className="text-lg font-bold text-orange-900">{evaluation.oi_ethical.toFixed(1)}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Full Report Modal */}
      <FullReportModal
        evaluation={selectedEvaluation}
        isOpen={showFullReportModal}
        onClose={() => {
          setShowFullReportModal(false);
          setSelectedEvaluation(null);
        }}
      />
    </div>
  );
};

export default MyEvaluations;
